import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, Circle, ExternalLink, Search, Globe2 } from 'lucide-react';

/**
 * Simple, plain-English tickable checklist for tradies to get their site
 * discoverable on Google. Ticks persist per-project in localStorage — no
 * backend required. Also includes the "Custom domain (coming soon)" nudge.
 */
const STEPS = [
  {
    id: 'gbp-claim',
    label: 'Create or claim your Google Business Profile',
    detail: 'It\'s free and puts you on Google Maps + Search when locals look for your trade.',
    link: 'https://www.google.com/business/',
    linkLabel: 'Go to Google Business',
  },
  {
    id: 'gbp-website',
    label: 'Add your YouBuild website URL to your Google Business Profile',
    detail: 'Paste the public share link into the "Website" field in your GBP dashboard.',
  },
  {
    id: 'nap',
    label: 'Keep name, phone and address (NAP) exactly the same everywhere',
    detail: 'The business name, phone and address on your site, GBP, Facebook and any listings must match word-for-word.',
  },
  {
    id: 'reviews-ask',
    label: 'Ask happy customers for a Google review',
    detail: 'Google reviews are the #1 ranking signal for local trades. Text your last 5 happy customers today.',
  },
  {
    id: 'posts',
    label: 'Post a photo or update to Google every 2-4 weeks',
    detail: 'From your GBP dashboard hit "Add update". A recent job photo works. Keeps your listing "fresh" in Google\'s eyes.',
  },
  {
    id: 'indexed',
    label: 'Confirm Google has indexed your page',
    detail: 'Search Google for site:your-share-link. If nothing shows after a week, submit the URL in Google Search Console.',
    link: 'https://search.google.com/search-console',
    linkLabel: 'Open Search Console',
  },
];

const storageKey = (projectId) => `yb.gfog.${projectId}`;

const GetFoundOnGoogle = ({ projectId }) => {
  const [ticked, setTicked] = useState({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey(projectId));
      setTicked(raw ? JSON.parse(raw) : {});
    } catch (_) { setTicked({}); }
  }, [projectId]);

  const toggle = useCallback((id) => {
    setTicked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(storageKey(projectId), JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, [projectId]);

  const doneCount = STEPS.filter((s) => ticked[s.id]).length;

  return (
    <div
      data-testid="get-found-panel"
      className="mt-6 bg-white rounded-2xl border p-5 sm:p-6"
      style={{ borderColor: 'var(--yb-border)' }}
    >
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>SEO checklist</p>
          <h3 className="text-[18px] font-semibold mt-0.5 flex items-center gap-1.5" style={{ color: 'var(--yb-ink)' }}>
            <Search size={16} style={{ color: 'var(--yb-amber)' }} /> Get found on Google
          </h3>
        </div>
        <span className="text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
          {doneCount} of {STEPS.length} done
        </span>
      </div>

      <ol className="space-y-3">
        {STEPS.map((s, idx) => {
          const done = !!ticked[s.id];
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => toggle(s.id)}
                data-testid={`gfog-step-${s.id}`}
                aria-pressed={done}
                className="w-full text-left flex items-start gap-3 p-3 rounded-xl border hover:bg-[#F5F4F0] transition-colors"
                style={{
                  borderColor: done ? 'rgba(79,70,229,0.35)' : 'var(--yb-border)',
                  backgroundColor: done ? 'rgba(79,70,229,0.05)' : 'white',
                }}
              >
                <span className="shrink-0 mt-0.5" style={{ color: done ? 'var(--yb-indigo)' : 'var(--yb-muted)' }}>
                  {done ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[14.5px] font-medium" style={{
                    color: done ? 'var(--yb-muted)' : 'var(--yb-ink)',
                    textDecoration: done ? 'line-through' : 'none',
                  }}>
                    {idx + 1}. {s.label}
                  </div>
                  <p className="text-[12.5px] mt-0.5" style={{ color: 'var(--yb-muted)' }}>{s.detail}</p>
                  {s.link && (
                    <a
                      href={s.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1.5 inline-flex items-center gap-1 text-[12.5px] underline decoration-dotted underline-offset-4"
                      style={{ color: 'var(--yb-indigo)' }}
                    >
                      {s.linkLabel} <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ol>

      {/* Custom domain nudge — placeholder until the full DNS pipeline ships. */}
      <div
        data-testid="custom-domain-placeholder"
        className="mt-5 rounded-xl border p-4 flex items-start gap-3"
        style={{ borderColor: 'var(--yb-amber)', backgroundColor: '#FFFBEB' }}
      >
        <Globe2 size={20} className="shrink-0 mt-0.5" style={{ color: 'var(--yb-amber)' }} />
        <div className="flex-1">
          <p className="text-[14px] font-semibold" style={{ color: '#92400E' }}>
            Connect your own domain (coming soon)
          </p>
          <p className="text-[12.5px] mt-0.5" style={{ color: '#B4530A' }}>
            A real domain like <code>yourbusiness.com.au</code> looks more professional and helps Google trust
            your site. We're building a simple, guided DNS setup — reply to any email to be notified when it lands.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GetFoundOnGoogle;
